import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import { ApiResponse } from '@/apis/types';

/** Board status from `shipment/services/monitor_arrivals.py`. */
export type ArrivalStatus = 'delivered' | 'late' | 'early' | 'on_time';

export interface MonitorArrival {
  id: string;
  reference_number: string;
  destination: {
    name: string;
    city: string;
    state: string;
    timezone: string | null;
  };
  courier_name: string;
  /** ISO 8601 timestamps. */
  scheduled_delivery_date: string | null;
  estimated_delivery_date: string | null;
  actual_delivery_date: string | null;
  status: ArrivalStatus;
  /** Signed minutes against schedule; positive means late. */
  minutes_vs_schedule: number | null;
}

/** A shipment due to be picked up soon (not picked up yet). */
export interface MonitorPickup {
  id: string;
  reference_number: string;
  origin: {
    name: string;
    city: string;
    state: string;
    timezone: string | null;
  };
  courier_name: string;
  /** ISO 8601. */
  scheduled_pickup_date: string | null;
}

export interface MonitorArrivalsPayload {
  generated_at: string;
  window_start: string;
  window_end: string;
  arrivals: MonitorArrival[];
  has_more: boolean;
  /**
   * Not picked up yet, pickup due from an hour ago to the window's end,
   * earliest first (at most 20).
   */
  pickups?: MonitorPickup[];
}

/** Arrivals board for /monitor: delivered in the last hour or landing in the next `hours`. */
export const GetMonitorArrivals = (
  token: string,
  hours = 4,
  teamIds?: string[],
  signal?: AbortSignal
): Promise<ApiResponse<MonitorArrivalsPayload>> =>
  interceptor.axiosGet(
    'Get',
    `${APIPaths.monitor_arrivals}?hours=${hours}${
      teamIds?.length
        ? `&teams=${teamIds.map((id) => encodeURIComponent(id)).join(',')}`
        : ''
    }`,
    token,
    null,
    signal ?? null,
    true
  ) as Promise<ApiResponse<MonitorArrivalsPayload>>;

export interface MonitorTripPlace {
  name: string;
  city: string;
  state: string;
  lat: number | null;
  lng: number | null;
  /** IANA zone cached on the location, when known. */
  timezone: string | null;
}

/** Payload of `GET /shipment/monitor/trip/<id>/` (ISO 8601 times). */
export interface MonitorTrip {
  id: string;
  reference_number: string;
  origin: MonitorTripPlace | null;
  destination: MonitorTripPlace | null;
  picked_up_at: string | null;
  scheduled_pickup_at: string | null;
  scheduled_delivery_at: string | null;
  /** Zoomlogi ETA, falling back to the schedule. */
  eta: string | null;
  delivered_at: string | null;
  /** Allowed range in °C (same unit as telemetry readings). */
  temperature: { min: number | null; max: number | null } | null;
  /**
   * Where it has been since pickup: origin, then courier scans and its sensor
   * track in time order (thinned). Empty before pickup.
   */
  path?: { lat: number; lng: number }[];
  /**
   * Where it is now: the newest timestamped position among its stored
   * position, its last scan since pickup and its sensor's last fix, none
   * after delivery; else the stored position, or before pickup the origin.
   * The TV places the circle here. Null: nothing places it.
   */
  now?: MonitorTripNow | null;
}

export interface MonitorTripNow {
  lat: number;
  lng: number;
  /** When it was recorded; null for an untimed stored position or the origin. */
  at: string | null;
  source: 'stored' | 'scan' | 'sensor' | 'origin';
}

/** Trip panel data for the shipment in focus on /monitor. */
export const GetMonitorTrip = (
  token: string,
  shipmentId: string,
  signal?: AbortSignal
): Promise<ApiResponse<MonitorTrip>> =>
  interceptor.axiosGet(
    'Get',
    `${APIPaths.monitor_trip}${encodeURIComponent(shipmentId)}/`,
    token,
    null,
    signal ?? null,
    true
  ) as Promise<ApiResponse<MonitorTrip>>;
